/**
 * UnifiedEventChart Component
 *
 * Multi-layer chart that displays:
 * - Sportsbook odds as lines (sharp vs square)
 * - Polymarket prices as dashed lines
 * - Price gap as filled area
 * - RLM signals as vertical markers
 * - Edge threshold annotations
 */

import React, { useMemo, useState, useCallback } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Brush
} from 'recharts';
import { NormalizedDataPoint } from '../../utils/chart-data-normalizer';

// ============================================
// Types
// ============================================

export interface UnifiedEventChartProps {
  data: NormalizedDataPoint[];
  homeTeam?: string;
  awayTeam?: string;
  showOdds?: boolean;
  showPolymarket?: boolean;
  showGap?: boolean;
  showSignals?: boolean;
  showSharpSquare?: boolean;
  edgeThreshold?: number;
  height?: number;
  enableBrush?: boolean;
}

interface ChartLayerConfig {
  visible: boolean;
  toggle: () => void;
  label: string;
  color: string;
}

// ============================================
// Color Palette
// ============================================

const COLORS = {
  // Odds lines
  oddsHome: '#3b82f6',      // Blue
  oddsAway: '#ef4444',      // Red
  sharpHome: '#1d4ed8',     // Dark blue
  sharpAway: '#b91c1c',     // Dark red
  squareHome: '#93c5fd',    // Light blue
  squareAway: '#fca5a5',    // Light red

  // Polymarket
  polyHome: '#10b981',      // Emerald
  polyAway: '#f59e0b',      // Amber

  // Gap area
  gapPositive: 'rgba(16, 185, 129, 0.3)',   // Green (poly > odds)
  gapNegative: 'rgba(239, 68, 68, 0.3)',    // Red (poly < odds)
  gapNeutral: 'rgba(156, 163, 175, 0.2)',   // Gray

  // Signals
  signalHome: '#8b5cf6',    // Purple
  signalAway: '#ec4899',    // Pink

  // Grid and axes
  grid: '#374151',
  axis: '#9ca3af',
  text: '#e5e7eb',

  // Edge threshold
  edgeThreshold: '#fbbf24'  // Yellow
};

// ============================================
// Custom Tooltip
// ============================================

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    dataKey: string;
    value: number;
    color: string;
    name: string;
    payload?: NormalizedDataPoint;
  }>;
  label?: string;
  homeTeam?: string;
  awayTeam?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  homeTeam,
  awayTeam
}) => {
  if (!active || !payload || payload.length === 0) return null;

  // Format timestamp
  const time = label ? new Date(label).toLocaleString() : '';

  // Find data point
  const dataPoint = payload[0]?.payload as NormalizedDataPoint | undefined;

  return (
    <div className="bg-gray-800 border border-gray-600 rounded-lg p-3 shadow-xl">
      <p className="text-gray-400 text-xs mb-2">{time}</p>

      {dataPoint && (
        <div className="space-y-2 text-sm">
          {/* Odds Section */}
          {(dataPoint.oddsHome !== null || dataPoint.oddsAway !== null) && (
            <div className="border-b border-gray-700 pb-2">
              <p className="text-gray-400 text-xs mb-1">Sportsbook Odds</p>
              {dataPoint.oddsHome !== null && (
                <p className="text-blue-400">
                  {homeTeam || 'Home'}: {dataPoint.oddsHome.toFixed(1)}%
                </p>
              )}
              {dataPoint.oddsAway !== null && (
                <p className="text-red-400">
                  {awayTeam || 'Away'}: {dataPoint.oddsAway.toFixed(1)}%
                </p>
              )}
            </div>
          )}

          {/* Polymarket Section */}
          {(dataPoint.polyHome !== null || dataPoint.polyAway !== null) && (
            <div className="border-b border-gray-700 pb-2">
              <p className="text-gray-400 text-xs mb-1">Polymarket</p>
              {dataPoint.polyHome !== null && (
                <p className="text-emerald-400">
                  {homeTeam || 'Home'}: {dataPoint.polyHome.toFixed(1)}%
                </p>
              )}
              {dataPoint.polyAway !== null && (
                <p className="text-amber-400">
                  {awayTeam || 'Away'}: {dataPoint.polyAway.toFixed(1)}%
                </p>
              )}
            </div>
          )}

          {/* Gap Section */}
          {(dataPoint.gapHome !== null || dataPoint.gapAway !== null) && (
            <div className="border-b border-gray-700 pb-2">
              <p className="text-gray-400 text-xs mb-1">Price Gap</p>
              {dataPoint.gapHome !== null && (
                <p className={dataPoint.gapHome >= 0 ? 'text-green-400' : 'text-red-400'}>
                  {homeTeam || 'Home'}: {dataPoint.gapHome >= 0 ? '+' : ''}{dataPoint.gapHome.toFixed(2)}%
                </p>
              )}
              {dataPoint.gapAway !== null && (
                <p className={dataPoint.gapAway >= 0 ? 'text-green-400' : 'text-red-400'}>
                  {awayTeam || 'Away'}: {dataPoint.gapAway >= 0 ? '+' : ''}{dataPoint.gapAway.toFixed(2)}%
                </p>
              )}
            </div>
          )}

          {/* Signal Section */}
          {dataPoint.signalStrength !== null && (
            <div>
              <p className="text-gray-400 text-xs mb-1">RLM Signal</p>
              <p className={dataPoint.signalDirection === 'home' ? 'text-purple-400' : 'text-pink-400'}>
                {dataPoint.signalDirection === 'home' ? homeTeam || 'Home' : awayTeam || 'Away'}:
                {' '}{dataPoint.signalStrength > 0 ? '+' : ''}{dataPoint.signalStrength.toFixed(0)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// Chart Legend Component
// ============================================

interface LayerToggleProps {
  layers: Record<string, ChartLayerConfig>;
}

const LayerToggle: React.FC<LayerToggleProps> = ({ layers }) => {
  return (
    <div className="flex flex-wrap gap-2 mb-3">
      {Object.entries(layers).map(([key, config]) => (
        <button
          key={key}
          onClick={config.toggle}
          className={`
            px-2 py-1 text-xs rounded-md flex items-center gap-1 transition-all
            ${config.visible
              ? 'bg-gray-700 text-white border border-gray-600'
              : 'bg-gray-800 text-gray-500 border border-gray-700'
            }
          `}
        >
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: config.visible ? config.color : '#6b7280' }}
          />
          {config.label}
        </button>
      ))}
    </div>
  );
};

// ============================================
// Signal Marker Component
// ============================================

interface SignalMarkerProps {
  cx?: number;
  cy?: number;
  payload?: NormalizedDataPoint;
}

const SignalMarker: React.FC<SignalMarkerProps> = ({ cx, cy, payload }) => {
  if (!cx || !cy || !payload?.signalStrength) return null;

  const strength = Math.abs(payload.signalStrength);
  const size = Math.min(strength / 10 + 4, 12); // 4-12px based on strength
  const color = payload.signalDirection === 'home' ? COLORS.signalHome : COLORS.signalAway;

  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={size}
        fill={color}
        fillOpacity={0.5}
        stroke={color}
        strokeWidth={2}
      />
      <circle
        cx={cx}
        cy={cy}
        r={size / 2}
        fill={color}
      />
    </g>
  );
};

// ============================================
// Main Component
// ============================================

export const UnifiedEventChart: React.FC<UnifiedEventChartProps> = ({
  data,
  homeTeam = 'Home',
  awayTeam = 'Away',
  showOdds = true,
  showPolymarket = true,
  showGap = true,
  showSignals = true,
  showSharpSquare = false,
  edgeThreshold = 3,
  height = 400,
  enableBrush = true
}) => {
  // Layer visibility state
  const [visibleLayers, setVisibleLayers] = useState({
    odds: showOdds,
    polymarket: showPolymarket,
    gap: showGap,
    signals: showSignals,
    sharpSquare: showSharpSquare
  });

  const toggleLayer = useCallback((layer: keyof typeof visibleLayers) => {
    setVisibleLayers(prev => ({ ...prev, [layer]: !prev[layer] }));
  }, []);

  // Layer configuration for toggle buttons
  const layerConfigs = useMemo((): Record<string, ChartLayerConfig> => ({
    odds: {
      visible: visibleLayers.odds,
      toggle: () => toggleLayer('odds'),
      label: 'Sportsbook',
      color: COLORS.oddsHome
    },
    polymarket: {
      visible: visibleLayers.polymarket,
      toggle: () => toggleLayer('polymarket'),
      label: 'Polymarket',
      color: COLORS.polyHome
    },
    gap: {
      visible: visibleLayers.gap,
      toggle: () => toggleLayer('gap'),
      label: 'Price Gap',
      color: '#10b981'
    },
    signals: {
      visible: visibleLayers.signals,
      toggle: () => toggleLayer('signals'),
      label: 'RLM Signals',
      color: COLORS.signalHome
    },
    sharpSquare: {
      visible: visibleLayers.sharpSquare,
      toggle: () => toggleLayer('sharpSquare'),
      label: 'Sharp/Square',
      color: COLORS.sharpHome
    }
  }), [visibleLayers, toggleLayer]);

  // Format X-axis tick
  const formatXAxis = useCallback((value: string) => {
    const date = new Date(value);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  // Calculate Y-axis domain
  const yDomain = useMemo(() => {
    const values: number[] = [];
    data.forEach(d => {
      if (visibleLayers.odds) {
        if (d.oddsHome !== null) values.push(d.oddsHome);
        if (d.oddsAway !== null) values.push(d.oddsAway);
      }
      if (visibleLayers.polymarket) {
        if (d.polyHome !== null) values.push(d.polyHome);
        if (d.polyAway !== null) values.push(d.polyAway);
      }
    });
    if (values.length === 0) return [0, 100];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.1 || 5;
    return [Math.max(0, min - padding), Math.min(100, max + padding)];
  }, [data, visibleLayers.odds, visibleLayers.polymarket]);

  // Check for signals to render
  const hasSignals = useMemo(() =>
    data.some(d => d.signalStrength !== null),
    [data]
  );

  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-gray-800 rounded-lg border border-gray-700"
        style={{ height }}
      >
        <p className="text-gray-400">No data available for chart</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
      {/* Layer Toggles */}
      <LayerToggle layers={layerConfigs} />

      {/* Chart Container */}
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={data}
          margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
        >
          {/* Gradient Definitions */}
          <defs>
            {/* Positive gap gradient (Polymarket > Odds) */}
            <linearGradient id="gapPositiveGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.polyHome} stopOpacity={0.4} />
              <stop offset="100%" stopColor={COLORS.polyHome} stopOpacity={0.1} />
            </linearGradient>

            {/* Negative gap gradient (Polymarket < Odds) */}
            <linearGradient id="gapNegativeGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.oddsHome} stopOpacity={0.1} />
              <stop offset="100%" stopColor={COLORS.oddsHome} stopOpacity={0.4} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />

          <XAxis
            dataKey="time"
            tickFormatter={formatXAxis}
            stroke={COLORS.axis}
            tick={{ fill: COLORS.text, fontSize: 11 }}
            interval="preserveStartEnd"
          />

          <YAxis
            domain={yDomain}
            tickFormatter={(v) => `${v.toFixed(0)}%`}
            stroke={COLORS.axis}
            tick={{ fill: COLORS.text, fontSize: 11 }}
            width={50}
          />

          {/* Edge Threshold Lines */}
          {edgeThreshold > 0 && visibleLayers.gap && (
            <>
              <ReferenceLine
                y={50 + edgeThreshold}
                stroke={COLORS.edgeThreshold}
                strokeDasharray="5 5"
                strokeWidth={1}
                label={{
                  value: `+${edgeThreshold}% edge`,
                  position: 'right',
                  fill: COLORS.edgeThreshold,
                  fontSize: 10
                }}
              />
              <ReferenceLine
                y={50 - edgeThreshold}
                stroke={COLORS.edgeThreshold}
                strokeDasharray="5 5"
                strokeWidth={1}
                label={{
                  value: `-${edgeThreshold}% edge`,
                  position: 'right',
                  fill: COLORS.edgeThreshold,
                  fontSize: 10
                }}
              />
            </>
          )}

          {/* Gap Area (between Polymarket and Odds) */}
          {visibleLayers.gap && (
            <Area
              type="monotone"
              dataKey="gapHome"
              fill="url(#gapPositiveGradient)"
              stroke="none"
              baseLine={0}
              isAnimationActive={false}
            />
          )}

          {/* Sharp Book Lines (if enabled) */}
          {visibleLayers.sharpSquare && (
            <>
              <Line
                type="monotone"
                dataKey="oddsSharpHome"
                stroke={COLORS.sharpHome}
                strokeWidth={2}
                strokeDasharray="8 4"
                dot={false}
                name={`${homeTeam} (Sharp)`}
                connectNulls
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="oddsSquareHome"
                stroke={COLORS.squareHome}
                strokeWidth={1}
                strokeDasharray="4 4"
                dot={false}
                name={`${homeTeam} (Square)`}
                connectNulls
                isAnimationActive={false}
              />
            </>
          )}

          {/* Sportsbook Odds Lines */}
          {visibleLayers.odds && (
            <>
              <Line
                type="monotone"
                dataKey="oddsHome"
                stroke={COLORS.oddsHome}
                strokeWidth={2}
                dot={false}
                name={`${homeTeam} (Odds)`}
                connectNulls
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="oddsAway"
                stroke={COLORS.oddsAway}
                strokeWidth={2}
                dot={false}
                name={`${awayTeam} (Odds)`}
                connectNulls
                isAnimationActive={false}
              />
            </>
          )}

          {/* Polymarket Lines */}
          {visibleLayers.polymarket && (
            <>
              <Line
                type="monotone"
                dataKey="polyHome"
                stroke={COLORS.polyHome}
                strokeWidth={2}
                strokeDasharray="6 3"
                dot={false}
                name={`${homeTeam} (Poly)`}
                connectNulls
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="polyAway"
                stroke={COLORS.polyAway}
                strokeWidth={2}
                strokeDasharray="6 3"
                dot={false}
                name={`${awayTeam} (Poly)`}
                connectNulls
                isAnimationActive={false}
              />
            </>
          )}

          {/* RLM Signal Markers */}
          {visibleLayers.signals && hasSignals && (
            <Line
              type="monotone"
              dataKey="signalStrength"
              stroke="transparent"
              dot={<SignalMarker />}
              isAnimationActive={false}
            />
          )}

          <Tooltip
            content={
              <CustomTooltip homeTeam={homeTeam} awayTeam={awayTeam} />
            }
          />

          <Legend
            wrapperStyle={{ paddingTop: 10 }}
            formatter={(value) => (
              <span style={{ color: COLORS.text, fontSize: 12 }}>{value}</span>
            )}
          />

          {/* Brush for zooming */}
          {enableBrush && data.length > 30 && (
            <Brush
              dataKey="time"
              height={30}
              stroke={COLORS.axis}
              fill="#1f2937"
              tickFormatter={formatXAxis}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default UnifiedEventChart;
